/**
 * Sentence-cases a line of copy without flattening the words written in capitals
 * on purpose.
 *
 * The site used to do this in CSS, with `lowercase first-letter:capitalize` on
 * the heading. That lowercases everything the browser paints, so an abbreviation
 * a heading depends on came out ruined: "CLO" as "clo", "KPI" as "kpi". This
 * walks the line word by word instead — a word whose capitals are its own is
 * left exactly as it stands, every other word is lowercased, and the first word
 * of each sentence is capitalized.
 *
 * A word's capitals are its own when it carries one anywhere but at its start:
 * "CLO" and "KPI", and equally the names written that way, "VanOnGo". A word
 * capitalized only at the front carries no such intent — it is either an ordinary
 * word or a Title Cased one — and is lowercased. A lone capital is kept, so the
 * English "I" survives.
 *
 * A word here is a run of letters, so "CLO-compliant" is judged in its two parts
 * and the punctuation between them is left alone. A script without letter case —
 * Japanese, on this site — has no capitals to keep and no case to change, so its
 * copy passes through untouched.
 *
 * Anything that is not a string comes back as it was given, so a heading may
 * still be handed a React node.
 */

const hasCase = (word) => word.toUpperCase() !== word.toLowerCase();

/** A word whose capitals were meant, and so is left exactly as it was written. */
const keepsItsCase = (word) =>
    word.slice(1) !== word.slice(1).toLowerCase()          // "CLO", "VanOnGo", "eBay"
    || (word.length === 1 && hasCase(word) && word === word.toUpperCase());  // "I"

const capitalizeFirst = (word) => word.charAt(0).toUpperCase() + word.slice(1);

/**
 * Whether the run of characters between two words closes a sentence. The mark has
 * to be the last thing in the run, give or take a closing quote or bracket, which
 * is what keeps a decimal ("1.5 hours") or a version from opening a new sentence.
 */
const endsSentence = (separator) => /[.!?。！？][\s"'’»)\]]*$/.test(separator);

export function capitalizeSmart(text) {
    if (typeof text !== 'string') return text;

    // Split keeps the separators, so odd indices are words and even ones are
    // whatever stood between them.
    const parts = text.split(/(\p{L}+)/u);
    let opensSentence = true;

    return parts
        .map((part, index) => {
            if (index % 2 === 0) {
                // A run carrying a figure has already begun the sentence, so the word
                // after it is not its first — "24 hours from now", "3 simple steps".
                if (endsSentence(part)) opensSentence = true;
                else if (/\d/.test(part)) opensSentence = false;
                return part;
            }
            const word = keepsItsCase(part) ? part : part.toLowerCase();
            const cased = opensSentence ? capitalizeFirst(word) : word;
            opensSentence = false;
            return cased;
        })
        .join('');
}
